import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore, type NavId } from '@/store/ui-store';

type SidebarProps = {
  /**
   * Slot map: which JSX to render inside the 240px sub-panel for each
   * nav id. Missing keys render an empty body. Allows App.tsx to keep
   * owning the actual content while the shell stays purely structural.
   */
  subPanels: Partial<Record<NavId, ReactNode>>;
};

type NavItem = {
  id: NavId;
  /** i18n key under `sidebar.nav.*`. */
  labelKey: string;
  /** Inline SVG path data — drawn inside a 16×16 viewBox. */
  icon: ReactNode;
};

const NAV_ITEMS: NavItem[] = [
  {
    id: 'data',
    labelKey: 'sidebar.nav.data',
    icon: (
      <>
        <path d="M8 10V3M5 6l3-3 3 3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 12h12" strokeWidth="1.5" strokeLinecap="round" />
      </>
    ),
  },
  {
    id: 'widget',
    labelKey: 'sidebar.nav.widget',
    icon: <path d="M8 3v10M3 8h10" strokeWidth="2" strokeLinecap="round" />,
  },
  {
    id: 'filter',
    labelKey: 'sidebar.nav.filter',
    icon: <path d="M2 4h12M4 7h8M6 10h4" strokeWidth="1.5" strokeLinecap="round" />,
  },
  {
    id: 'dataconf',
    labelKey: 'sidebar.nav.dataconf',
    icon: (
      <>
        <rect x="2" y="2" width="12" height="12" rx="1" strokeWidth="1.4" fill="none" />
        <path d="M2 6h12M2 10h12M6 2v12" strokeWidth="1.2" />
      </>
    ),
  },
  {
    id: 'dashboard',
    labelKey: 'sidebar.nav.dashboard',
    icon: (
      <>
        <rect x="2" y="2" width="5" height="5" rx="1" strokeWidth="1.4" fill="none" />
        <rect x="9" y="2" width="5" height="5" rx="1" strokeWidth="1.4" fill="none" />
        <rect x="2" y="9" width="5" height="5" rx="1" strokeWidth="1.4" fill="none" />
        <rect x="9" y="9" width="5" height="5" rx="1" strokeWidth="1.4" fill="none" />
      </>
    ),
  },
  {
    id: 'appearance',
    labelKey: 'sidebar.nav.appearance',
    icon: (
      <>
        <circle cx="8" cy="8" r="6" strokeWidth="1.5" fill="none" />
        <path
          d="M8 2c-2 2-3 3.6-3 6s1 4 3 6M8 2c2 2 3 3.6 3 6s-1 4-3 6M2 8h12"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </>
    ),
  },
];

/**
 * Sidebar shell — 64px nav rail + 240px sub-panel that opens beside
 * it when a nav item is active. Privacy lives at the rail bottom as a
 * modal trigger (not a sub-panel). Sub-panel content is supplied via
 * the `subPanels` slot prop.
 *
 * State lives in useUiStore: `activeNav` drives which sub-panel is
 * shown; mutually exclusive with the right panel (set by setActiveNav).
 */
export function Sidebar({ subPanels }: SidebarProps): JSX.Element {
  const { t } = useTranslation();
  const activeNav = useUiStore((s) => s.activeNav);
  const setActiveNav = useUiStore((s) => s.setActiveNav);
  const setPrivacyOpen = useUiStore((s) => s.setPrivacyModalOpen);

  // ESC closes the sub-panel.
  useEffect(() => {
    if (activeNav === null) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setActiveNav(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeNav, setActiveNav]);

  const handleNavClick = (id: NavId): void => {
    setActiveNav(activeNav === id ? null : id);
  };

  const activeItem = NAV_ITEMS.find((n) => n.id === activeNav);

  return (
    <>
      {/* 64px rail — always visible */}
      <nav
        aria-label={t('sidebar.rail')}
        className="shrink-0 flex flex-col items-center gap-1 pt-2 overflow-y-auto"
        style={{
          width: 'var(--rail-width)',
          background: 'var(--color-surface)',
          borderRight: '1px solid var(--color-border)',
        }}
      >
        {/* Brand icon */}
        <div
          className="flex items-center justify-center rounded-lg shrink-0 mb-2"
          style={{
            width: 36,
            height: 36,
            background: 'var(--color-accent)',
          }}
          aria-hidden
        >
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" style={{ color: '#fff' }}>
            <rect x="2" y="7" width="3" height="7" rx="1" fill="currentColor" />
            <rect x="6.5" y="4" width="3" height="10" rx="1" fill="currentColor" />
            <rect x="11" y="1.5" width="3" height="12.5" rx="1" fill="currentColor" />
          </svg>
        </div>

        {NAV_ITEMS.map((item) => {
          const isActive = activeNav === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleNavClick(item.id)}
              title={t(item.labelKey)}
              className="flex flex-col items-center justify-center gap-0.5 rounded-lg border-0 cursor-pointer"
              style={{
                width: 58,
                padding: '7px 3px',
                background: isActive ? 'var(--color-active)' : 'transparent',
                color: isActive ? 'var(--color-accent)' : 'var(--color-muted)',
                transition: 'background-color 0.12s, color 0.12s',
              }}
              aria-pressed={isActive}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                style={{ flexShrink: 0 }}
                aria-hidden
              >
                {item.icon}
              </svg>
              <span
                className="text-center leading-tight w-full"
                style={{
                  fontSize: '8.5px',
                  fontWeight: isActive ? 700 : 500,
                  whiteSpace: 'normal',
                  // `normal` keeps single-word labels (Appearance, Dashboard)
                  // on a single line; multi-word labels still wrap at the
                  // space between words.
                  wordBreak: 'normal',
                  overflowWrap: 'normal',
                  hyphens: 'none',
                }}
              >
                {t(item.labelKey)}
              </span>
            </button>
          );
        })}

        <div className="flex-1" />

        {/* Privacy trigger — opens PrivacyModal, not a sub-panel */}
        <button
          type="button"
          onClick={() => setPrivacyOpen(true)}
          title={t('privacy.indicatorLabel')}
          className="flex flex-col items-center justify-center gap-0.5 rounded-lg border-0 cursor-pointer mb-2"
          style={{
            width: 52,
            padding: '7px 4px',
            background: 'transparent',
            color: 'var(--color-success)',
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <rect x="3" y="8" width="10" height="7" rx="1.5" />
            <path d="M5 8V5.5a3 3 0 016 0V8" />
          </svg>
          <span
            className="text-center leading-tight"
            style={{ fontSize: '8px', fontWeight: 600 }}
          >
            {t('privacy.indicatorLabel')}
          </span>
        </button>
      </nav>

      {/* 240px sub-panel — only when a nav item is active */}
      {activeItem && (
        <aside
          className="shrink-0 flex flex-col overflow-y-auto"
          style={{
            width: 'var(--subpanel-width)',
            background: 'var(--color-bg)',
            borderRight: '1px solid var(--color-border)',
          }}
        >
          <header
            className="flex items-center justify-between px-3.5 py-2.5 border-b shrink-0"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <span className="text-xs font-bold" style={{ color: 'var(--color-fg)' }}>
              {t(activeItem.labelKey)}
            </span>
            <button
              type="button"
              onClick={() => setActiveNav(null)}
              className="flex items-center justify-center p-0.5 border-0 bg-transparent cursor-pointer hover:opacity-80"
              style={{ color: 'var(--color-muted)' }}
              aria-label={t('sidebar.close')}
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </header>
          <div className="flex-1 p-3.5">{subPanels[activeItem.id]}</div>
        </aside>
      )}
    </>
  );
}
